import { format } from 'date-fns'
import { t } from 'i18next'
import React from 'react'
import { Trans } from 'react-i18next'

import { ClaimDamageLocation, ClaimStatus, ClaimType } from '../../common/enums.js'
import { SubscriptionClaimDTO } from '../../common/types.js'
import { formatMonthlyPrice } from '../../common/utils/utils.js'
import { Accordion, AccordionItem } from '../components/accordion/accordion.js'
import { Button } from '../components/button/button.js'
import { Card } from '../components/card/card.js'
import { GridColumn } from '../components/grid/grid-column.js'
import { Grid } from '../components/grid/grid.js'
import { CheckCircleIcon, FlexIcon, WarningCircleIcon } from '../components/icon/icon.js'
import { ListItemProps } from '../components/list/list.js'
import { SummaryCard, SwapSummaryItem } from '../views/portal/swap/swap.js'
import { SubscriptionProduct } from './subscription.js'

export const getCommonSubscriptionCardDetailListItems = (
    product: SubscriptionProduct,
    title: JSX.Element,
    upgrade?: boolean,
    upgradeBy?: string,
) => {
    const items: Array<ListItemProps | undefined> = [
        {
            children: title,
            icon: FlexIcon,
        },
    ]

    if (product.subscription.hasEnded) {
        items.push(
            {
                children: (
                    <>
                        {t('subscription.contractStarted')}{' '}
                        <strong>{product.subscription.formattedContractStartDate}</strong>
                    </>
                ),
            },
            {
                children: (
                    <>
                        {t('subscription.contractEnded')}{' '}
                        <strong>{product.subscription.formattedContractEndDate}</strong>
                    </>
                ),
            },
        )

        return items.filter(Boolean)
    }

    items.push({
        children: (
            <Grid align={['middle-xs']} noWrap={true}>
                <GridColumn width={['max-xs']}>
                    {t('subscription.contractSigned')} <strong>{product.subscription.formattedSignedDate}</strong>
                </GridColumn>
                {product.subscription.contractPdfUrl && (
                    <GridColumn width={['min-xs']}>
                        <Button
                            url={product.subscription.contractPdfUrl}
                            style="underlined"
                            text={t('subscription.viewContract')}
                            target="_blank"
                        />
                    </GridColumn>
                )}
            </Grid>
        ),
    })

    const currentClaim = product.subscription.mainProduct.claimCurrent

    if (currentClaim) {
        switch (currentClaim.claimType) {
            case ClaimType.SWAP_BOLTTECH:
                items.push({
                    children: (
                        <Grid align={['middle-xs']}>
                            <GridColumn width={['xs-12']}>
                                <Accordion variant="faq">
                                    <AccordionItem
                                        id="swap-overview-accordion-item"
                                        label={<h2 className="h4">{t('subscription.swap.summary.accordionTitle')}</h2>}
                                    >
                                        {currentClaim.redirectUrl && (
                                            <GridColumn width={['xs-12']} className="h-text-center">
                                                <Button
                                                    url={currentClaim.redirectUrl}
                                                    text={t('subscription.swap.bolttech.continueButton')}
                                                />
                                            </GridColumn>
                                        )}
                                    </AccordionItem>
                                </Accordion>
                            </GridColumn>
                        </Grid>
                    ),
                })
                break
            case ClaimType.SWAP:
                items.push({
                    children: (
                        <Grid align={['middle-xs']}>
                            <GridColumn width={['xs-12']}>
                                <Accordion variant="faq">
                                    <AccordionItem
                                        id="swap-overview-accordion-item"
                                        label={<h2 className="h4">{t('subscription.swap.summary.accordionTitle')}</h2>}
                                    >
                                        <SummaryCard
                                            {...{
                                                small: true,
                                                summary: {
                                                    items: getSwapSummaryItems(product, currentClaim),
                                                },
                                            }}
                                        />
                                        {(currentClaim.claimStatus === ClaimStatus.PENDING ||
                                            currentClaim.claimStatus === ClaimStatus.WAITINGFORPAYMENT ||
                                            currentClaim.claimStatus === ClaimStatus.PAYMENTERROR) && (
                                            <GridColumn width={['xs-12']} className="h-text-center">
                                                <Button
                                                    url={product.subscription.getClaimPayUrl(currentClaim.id)}
                                                    text={t('subscription.swap.payButton')}
                                                />
                                            </GridColumn>
                                        )}
                                    </AccordionItem>
                                </Accordion>
                            </GridColumn>
                            {currentClaim.claimStatus === ClaimStatus.RETURNKITSENT && (
                                <GridColumn width={['xs-12']}>
                                    <Card hasContent={true} padding="medium" variant="quinary">
                                        <div className="text">
                                            <h3 className="h4">
                                                {t('subscription.swap.summary.returnKitSentNotificationTitle')}
                                            </h3>
                                            <p className="text__margin-small">
                                                {t('subscription.swap.summary.returnKitSentNotificationContent')}
                                            </p>
                                            <a
                                                href={t('subscription.swap.summary.returnKitSentNotificationLink')}
                                                className="text__link"
                                            >
                                                {t('subscription.swap.summary.returnKitSentNotificationLinkText')}
                                            </a>
                                        </div>
                                    </Card>
                                </GridColumn>
                            )}
                        </Grid>
                    ),
                })
                break
        }
    } else {
        items.push(
            upgrade
                ? {
                      children: t('subscription.upgradeAnyTime'),
                      icon: CheckCircleIcon,
                  }
                : undefined,
            upgradeBy && !upgrade
                ? {
                      children: (
                          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                          // @ts-ignore ReactI18N type definition issue
                          <Trans i18nKey="subscription.upgradeFrom">
                              Eligible for upgrade from{' '}
                              <strong
                                  dangerouslySetInnerHTML={{
                                      __html: upgradeBy,
                                  }}
                              ></strong>
                          </Trans>
                      ),
                      icon: WarningCircleIcon,
                  }
                : undefined,
            {
                children: (
                    <Grid align={['middle-xs']} noWrap={true}>
                        <GridColumn width={['max-xs']}>
                            <p>
                                <strong>
                                    {formatMonthlyPrice(
                                        product.baseSubscriptionCostGross,
                                        product.currencySymbol,
                                        product.locale,
                                    )}
                                </strong>
                            </p>
                        </GridColumn>
                        <GridColumn width={['min-xs']}>
                            <Button
                                url={product.subscription.invoicesUrl}
                                style="underlined"
                                text={t('portal.invoices.viewInvoices')}
                            />
                        </GridColumn>
                    </Grid>
                ),
            },
        )
    }

    return items.filter(Boolean)
}

const getSwapSummaryItems = (product: SubscriptionProduct, claim: SubscriptionClaimDTO): SwapSummaryItem[] => {
    const items: SwapSummaryItem[] = []

    items.push({
        title: t('subscription.swap.summary.productTitle'),
        values: [
            `${product.subscription.mainProduct.data.product?.translation?.name}, ${product.subscription.mainProduct.data.product?.description}`,
        ],
    })

    items.push({
        title: t('subscription.swap.summary.certificateTitle'),
        values: [`${claim.visualID}`],
    })

    items.push({
        title: t('subscription.swap.summary.customerTitle'),
        values: [`${product.subscription.mainProduct.customer?.fullName}`],
    })

    if (product.subscription.contractStartDate) {
        items.push({
            title: t('subscription.swap.summary.periodTitle'),
            values: [`${format(product.subscription.contractStartDate, 'dd.MM.yyyy')}`],
        })
    }

    items.push({
        title: t('subscription.swap.summary.deviceDetailsTitle'),
        values: [`${product.subscription.mainProduct.sku}`, `${product.subscription.mainProduct.data.productImei}`],
    })

    items.push({
        title: t('subscription.swap.summary.overviewTitle'),
        subValues: [
            {
                title: t('subscription.swap.summary.overviewIssueTitle'),
                value: t(`subscription.swap.summary.overview.issues.${claim.damageType}`),
            },
            claim.incidentDescription
                ? {
                      title: t('subscription.swap.summary.overviewDamageDescriptionTitle'),
                      value: `${claim.incidentDescription}`,
                  }
                : undefined,
            claim.damageLocations?.length
                ? {
                      title: t('subscription.swap.summary.overviewDamagesTitle'),
                      list: claim.damageLocations.map((damageLocation) => {
                          if (
                              damageLocation.damageLocation === ClaimDamageLocation.OTHER &&
                              damageLocation.description
                          ) {
                              return `<strong>${t(
                                  `subscription.swap.summary.${damageLocation.damageLocation}`,
                              )}</strong><br>${damageLocation.description}`
                          }

                          if (
                              (damageLocation.damageLocation === ClaimDamageLocation.FRONTSIDE ||
                                  damageLocation.damageLocation === ClaimDamageLocation.BACKSIDE) &&
                              damageLocation.images
                          ) {
                              return `<strong>${t(
                                  `subscription.swap.summary.${damageLocation.damageLocation}`,
                              )}</strong> ${t('general.images', {
                                  count: damageLocation.images.length,
                              })}`
                          }

                          return `<strong>${t(`subscription.swap.summary.${damageLocation.damageLocation}`)}</strong>`
                      }),
                  }
                : undefined,
        ].filter(Boolean),
    })

    return items
}
